<template>
  <div>
    <div class="container internal-section">
      <LoadingComponent v-if="isLoading" class="loader" />
      <div v-else-if="call">
        <q-breadcrumbs class="q-mb-md text-subtitle2 text-primary">
          <q-breadcrumbs-el :label="$t('call_details.breadcrumb.home')" icon="home" to="/" />
          <q-breadcrumbs-el
            :label="$t('call_details.breadcrumb.programs')"
            icon="book"
            to="/programmes"
          />
          <q-breadcrumbs-el
            icon="school"
            :to="`/programmes/${toRouteType(call.call_type)}/${call.programme_id}/details`"
            :label="getCallProgramme(call)"
          />
          <q-breadcrumbs-el icon="info" :label="getCallTitle(call)" />
        </q-breadcrumbs>

        <q-card class="detail-card q-mb-md shadow-2">
          <q-card-section class="secondary-background header row items-center justify-between">
            <div class="col-12 col-sm-8 column justify-center">
              <div class="title text-overline text-primary">
                {{ getCallType(call) }} • {{ $t('call_details.academic_year') }}:
                {{ call.academic_year }}
              </div>

              <div class="text-h5 text-primary text-weight-bold">{{ getCallTitle(call) }}</div>
              <div class="text-subtitle1 text-primary q-mt-xs flex items-center">
                {{ $t('call_details.programme') }}: {{ getCallProgramme(call) }}
                <q-btn
                  :to="`/programmes/${toRouteType(call.call_type)}/${call.programme_id}/details`"
                  target="_blank"
                  flat
                  dense
                  round
                  icon="open_in_new"
                ></q-btn>
              </div>
            </div>

            <div
              v-if="call.target_languages?.length"
              class="col-12 col-sm-4 flex items-center q-gutter-xs"
              :class="$q.screen.lt.sm ? 'justify-center q-mt-md' : 'justify-end'"
            >
              <q-chip
                v-for="lang in call.target_languages"
                :key="lang"
                dense
                square
                color="white"
                text-color="primary"
                class="language-chip"
              >
                <q-avatar>
                  <img :src="getFlagUrl(lang)" :alt="lang" />
                </q-avatar>
                {{ getLanguageLabel(lang) }}
              </q-chip>
            </div>
          </q-card-section>

          <q-card-section class="bg-grey-1 row">
            <div class="col-12 col-sm-4 row items-center">
              <q-icon name="event_available" color="primary" size="sm" class="q-mr-sm" />
              <div>
                <div class="text-caption text-grey-7">{{ $t('call_details.publish_date') }}</div>
                <div class="text-weight-medium">{{ getCallPublishDate(call) }}</div>
              </div>
            </div>
            <div class="col-12 col-sm-4 row items-center">
              <q-icon name="today" color="green" size="sm" class="q-mr-sm" />
              <div>
                <div class="text-caption text-grey-7">{{ $t('call_details.start_date') }}</div>
                <div class="text-weight-medium">{{ getCallStartDate(call) }}</div>
              </div>
            </div>
            <div class="col-12 col-sm-4 row items-center">
              <q-icon name="event_busy" color="negative" size="sm" class="q-mr-sm" />
              <div>
                <div class="text-caption text-grey-7">
                  {{ $t('call_details.end_date') }} / {{ $t('call_details.deadline') }}
                </div>
                <div class="text-weight-medium">{{ getCallEndDate(call) }}</div>
              </div>
            </div>
          </q-card-section>

          <q-separator />

          <q-card-section>
            <div class="text-subtitle2 text-grey-8 q-mb-xs">
              {{ $t('call_details.description') }}
            </div>
            <div class="text-body1 text-justify raw-html" v-html="getCallDescription(call)"></div>
          </q-card-section>

          <q-card-section class="q-pt-none column q-gutter-sm">
            <div v-if="call.disciplines && call.disciplines.length > 0">
              <div class="text-caption text-grey-7 q-mb-xs">
                {{ $t('call_details.disciplines.title') }}
              </div>
              <div class="row q-gutter-xs">
                <q-chip
                  v-for="discipline in call.disciplines"
                  :key="discipline.discipline_id"
                  dense
                  color="blue-grey-1"
                  text-color="primary"
                  icon="alt_route"
                  class="discipline-chip"
                >
                  <span class="ellipsis">{{ getCallDisciplineTitle(discipline) }}</span>
                  <q-tooltip>{{ getCallDisciplineTitle(discipline) }}</q-tooltip>
                </q-chip>
              </div>
            </div>

            <q-banner
              v-if="showLanguageMismatchWarning"
              rounded
              class="bg-orange-1 text-orange-10 shadow-1"
            >
              <template v-slot:avatar>
                <q-icon name="warning" />
              </template>
              {{
                $t('call_details.language_mismatch_warning', {
                  languages: call.target_languages.join(', '),
                })
              }}
            </q-banner>

            <div class="row items-center justify-between q-gutter-sm">
              <q-badge color="blue-grey-7" outline class="q-pa-sm text-subtitle2 rounded-borders">
                <q-icon name="groups" class="q-mr-xs" />
                {{ $t('call_details.admissions') }}: {{ call.number_of_admissions }}
              </q-badge>

              <div class="row q-gutter-sm">
                <q-btn
                  v-if="call.is_open && call.programme_apply_status && isEligibleForCall(call)"
                  color="secondary"
                  unelevated
                  rounded
                  size="md"
                  no-caps
                  icon="fact_check"
                  :label="$t('call_details.apply')"
                  class="apply-pulse-btn"
                  @click="apply"
                />
                <q-btn
                  v-if="call.call_url && !call.programme_apply_status"
                  color="secondary"
                  unelevated
                  rounded
                  size="md"
                  no-caps
                  icon="fact_check"
                  :label="$t('call_details.visit_call')"
                  :href="call.call_url"
                  target="_blank"
                />
                <q-btn
                  v-if="call.call_file_url"
                  outline
                  color="secondary"
                  rounded
                  no-caps
                  icon="file_open"
                  :label="$t('call_details.call_file')"
                  target="_blank"
                  @click="openFile(call.call_file_url)"
                />
              </div>
            </div>
          </q-card-section>
        </q-card>
        <div>
          <div v-if="call.requested_documents.length > 0">
            <div class="text-h6 q-my-md text-primary row items-center">
              <q-icon name="description" color="secondary" class="q-mr-sm" />
              {{ $t('call_details.documents.title', 'Required Documents') }}
              <q-badge color="secondary" class="q-ml-sm" v-if="!isLoadingDocs">
                {{ call.requested_documents?.length || 0 }}
              </q-badge>
              <q-spinner class="q-ml-sm" size="xs" color="secondary" v-else />
            </div>

            <div class="row q-col-gutter-md">
              <div v-for="doc in call.requested_documents" :key="doc.id" class="col-12 col-sm-6">
                <q-card flat bordered class="column justify-between full-height document-card">
                  <q-card-section>
                    <div class="row items-start justify-between no-wrap q-mb-xs">
                      <div class="text-subtitle1 text-weight-bold text-primary text-line-clamp">
                        {{ getCallRequestedDocumentTypeLabel(doc) }}
                      </div>
                      <q-badge :color="doc.is_required ? 'orange' : 'warning'" text-color="white">
                        {{
                          doc.is_required
                            ? $t('call_details.documents.required', 'Required')
                            : $t('call_details.documents.optional', 'Optional')
                        }}
                      </q-badge>
                    </div>

                    <p
                      class="text-body2 text-grey-8 text-justify q-mb-none"
                      v-if="getCallRequestedDocumentDescription(doc)"
                    >
                      {{ getCallRequestedDocumentDescription(doc) }}
                    </p>
                  </q-card-section>

                  <q-separator class="mt-auto" />

                  <q-card-actions
                    class="bg-grey-1 text-caption text-grey-7 row justify-between q-px-md"
                  >
                    <div>
                      <q-icon name="filter_none" class="q-mr-xs" />
                      {{ $t('call_details.documents.max_files', 'Max Files') }}:
                      <span class="text-weight-bold text-dark">{{ doc.max_files }}</span>
                    </div>
                  </q-card-actions>
                </q-card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <FilePreviewDialog
      v-model="previewDialog"
      :title="$t('call_details.call_file')"
      :iframe-url="iframeUrl"
    />
    <ApplicantSelectionModal
      v-if="authStore.isAuthenticated && authStore.isAgent"
      v-model="selectionModal"
      @selected="onApplicantSelected"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useQuasar } from 'quasar'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'

import { useCallLocale } from 'src/composables/useCallLocale'
import { useSeoMeta } from 'src/composables/useSeoMeta'
import { useFilePreview } from 'src/composables/useFilePreview'
import { useLanguageFlags } from 'src/composables/useLanguageFlags'
import { useApplicationEligibility } from 'src/composables/useApplicationEligibility'

import FilePreviewDialog from 'src/components/common/FilePreviewDialog.vue'
import ApplicantSelectionModal from 'src/components/applicant/ApplicantSelectionModal.vue'
import LoadingComponent from 'src/components/main/LoadingComponent.vue'

import { useAuthStore } from 'src/stores/auth-store'
import { useCallStore } from 'src/stores/call-store'
import { toRouteType } from 'src/utils/programmeTypeMapper'

const { t, locale } = useI18n()
const $q = useQuasar()

const router = useRouter()
const route = useRoute()
const callStore = useCallStore()
const authStore = useAuthStore()
const selectionModal = ref(false)

const { selectedCall: call, isLoading, isLoadingDocs } = storeToRefs(callStore)
const { previewDialog, iframeUrl, openFile } = useFilePreview()
const { getFlagUrl } = useLanguageFlags()
const { ensureEligibilityLoaded, isEligibleForCall } = useApplicationEligibility()

const {
  getCallTitle,
  getCallDescription,
  getCallType,
  getCallProgramme,
  getCallStartDate,
  getCallEndDate,
  getCallPublishDate,
  getCallRequestedDocumentTypeLabel,
  getCallRequestedDocumentDescription,
  getCallDisciplineTitle,
  getLanguageLabel,
} = useCallLocale()

const currentSiteLanguage = computed(() => (locale.value.startsWith('el') ? 'Greek' : 'English'))

const showLanguageMismatchWarning = computed(() => {
  if (!call.value?.target_languages?.length) return false
  return !call.value.target_languages.includes(currentSiteLanguage.value)
})

useSeoMeta({
  title: () => {
    return call.value ? getCallTitle(call.value) : t('loading', 'Loading...')
  },

  description: () => {
    if (!call.value) return ''
    return getCallDescription(call.value)
      .replace(/<[^>]*>?/gm, '')
      .substring(0, 160)
  },

  image: () => undefined,

  type: 'article',

  noindex: false,

  schema: () => ({
    type: 'WebPage',
    data: call.value
      ? {
          name: getCallTitle(call.value),
          description: getCallDescription(call.value).replace(/<[^>]*>?/gm, ''),
          mainEntity: {
            '@type': 'Action',
            name: 'Review Call Details and Required Documents',
            agent: {
              '@type': 'Organization',
              name: 'AtSiG',
            },
          },
        }
      : {},
  }),
})

onMounted(async () => {
  const callId = Number(route.params.id)

  try {
    const isInternal = (route.meta.isInternal ?? true) as boolean
    await callStore.initCall(callId, isInternal)
    await ensureEligibilityLoaded()
  } catch (error) {
    console.error('Fetch failed in onMounted:', error)
  }
})

async function apply() {
  if (!call.value) return
  if (!authStore.isAuthenticated) {
    await router.push({
      name: 'Application Create',
      params: { id: call.value.id, lang: route.params.lang || 'en' },
    })
  } else {
    if (authStore.isAgent) {
      selectionModal.value = true
    }
    if (authStore.isApplicant)
      await router.push({ name: 'Application Create', params: { id: call.value.id } })
    if (authStore.isSecretary)
      $q.notify({
        color: 'negative',
        message: 'You are signed in as Secretary, you cannot apply to program!',
        timeout: 2000,
      })
  }
}

const onApplicantSelected = async (uuid: string) => {
  if (!call.value) return
  authStore.setActingAs(uuid, $q.cookies)
  await router.push({
    name: 'Agent Applicant Application Create',
    params: { uuid: uuid, id: call.value.id },
  })
}
</script>

<style lang="scss" scoped>
.document-card {
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}
.document-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}
.text-line-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.raw-html :deep(p) {
  margin-bottom: 8px;
}
.mt-auto {
  margin-top: auto;
}

.discipline-chip {
  max-width: 220px;
}
.discipline-chip .ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.language-chip img {
  object-fit: cover;
}

.apply-pulse-btn {
  animation: pulse-glow 1.8s ease-in-out infinite;
  --pulse-color: var(--q-secondary);
}
</style>
